/**
 * MobileAddContractModal
 *
 * MUI full-screen Dialog replacement for AddContractModal (PrimeReact Dialog).
 * Resolves z-index issues when opened inside MobileCollabModalDetails SwipeableDrawer.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, Autocomplete,
 *   TextField, Button, Box
 */
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Box,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Redux
import {
  getCollabContract,
  addCollabContract,
  typeOfContractOptions,
} from "../../../components/collabs/collabsSlice";

// Shared utilities
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& input::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddContractModal(props) {
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.collabs.companies);
  const allCollabs = useSelector((state) => state.collabs.allCollabsList);

  const [newContract, setNewContract] = useState({});

  const handleClose = () => {
    props.onHide();
    setNewContract({});
  };

  const isFormValid = () => {
    return newContract.type_contrat && newContract.entree_date;
  };

  const handleAddContract = async () => {
    try {
      const actionResponse = await dispatch(
        addCollabContract({
          collab_contract: {
            id_collab: props.collabDetails.id,
            id_company: newContract.id_company,
            type_contrat: newContract.type_contrat,
            entree_date: newContract.entree_date
              ? moment(newContract.entree_date).format("YYYY-MM-DD HH:mm:ss")
              : null,
            sortie_date: newContract.sortie_date
              ? moment(newContract.sortie_date).format("YYYY-MM-DD HH:mm:ss")
              : null,
            responsable_contractuel: newContract.responsable_contractuel,
          },
        })
      ).unwrap();
      if (actionResponse.responseData?.error == "conflict") {
        showErrorToast("CONTRACT_CONFLICT_ERROR");
      } else if (actionResponse.responseData?.status) {
        dispatch(getCollabContract({ id_collab: props.collabDetails.id }));
        props.onHide();
        setNewContract({});
      }
    } catch (error) {
      console.error("Error adding contract:", error);
      showErrorToast();
    }
  };

  return (
    <Dialog
      fullScreen
      open={!!props.show}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      {/* Top AppBar */}
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="ADD_CONTRACT" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Form Content */}
      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {/* Company */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="COMPANY" />
          </Typography>
          <Autocomplete
            options={companies || []}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              (companies || []).find(
                (o) => o.value === newContract.id_company
              ) || null
            }
            onChange={(_, newVal) =>
              setNewContract((prev) => ({
                ...prev,
                id_company: newVal ? newVal.value : null,
              }))
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner une entreprise"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Contract Type */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="CONTRACT_TYPE" />
          </Typography>
          <Autocomplete
            options={typeOfContractOptions || []}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              (typeOfContractOptions || []).find(
                (o) => o.value === newContract.type_contrat
              ) || null
            }
            onChange={(_, newVal) =>
              setNewContract((prev) => ({
                ...prev,
                type_contrat: newVal ? newVal.value : null,
              }))
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type de contrat"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Responsable Contractuel */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="RESPONSABLE_CONTRACTUEL" />
          </Typography>
          <Autocomplete
            options={allCollabs || []}
            getOptionLabel={(opt) => opt.label || ""}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            value={
              (allCollabs || []).find(
                (o) => o.value === newContract.responsable_contractuel
              ) || null
            }
            onChange={(_, newVal) =>
              setNewContract((prev) => ({
                ...prev,
                responsable_contractuel: newVal ? newVal.value : null,
              }))
            }
            slotProps={{ popper: { sx: { zIndex: 1500 } } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner un responsable"
                size="small"
                sx={glassmorphicTextFieldSx}
              />
            )}
            fullWidth
          />

          {/* Start Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="START_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={
              newContract.entree_date
                ? moment(newContract.entree_date).format("YYYY-MM-DD")
                : ""
            }
            onChange={(e) =>
              setNewContract((prev) => ({
                ...prev,
                entree_date: e.target.value ? new Date(e.target.value) : null,
              }))
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />

          {/* End Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="END_DATE" />
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            disabled={!newContract.entree_date}
            value={
              newContract.sortie_date
                ? moment(newContract.sortie_date).format("YYYY-MM-DD")
                : ""
            }
            inputProps={{
              min: newContract.entree_date
                ? moment(newContract.entree_date).format("YYYY-MM-DD")
                : undefined,
            }}
            onChange={(e) =>
              setNewContract((prev) => ({
                ...prev,
                sortie_date: e.target.value ? new Date(e.target.value) : null,
              }))
            }
            InputLabelProps={{ shrink: true }}
            sx={glassmorphicTextFieldSx}
          />
        </Box>
      </Box>

      {/* Sticky bottom button */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          zIndex: 1,
        }}
      >
        <Button
          variant="contained"
          color="warning"
          fullWidth
          disabled={!isFormValid()}
          onClick={handleAddContract}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1.05rem",
            letterSpacing: "-0.01em",
            boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            "&.Mui-disabled": {
              boxShadow: "none",
            },
          }}
        >
          <Langs str="ADD" />
        </Button>
      </Box>
    </Dialog>
  );
}
